/* eslint-disable import/no-restricted-paths */
import React from 'react';
import { Box, Chip, HorizontalScroll, TextLink } from '@drone-js/core';
import { selectTopSubcategoriesFromBootstrap } from 'store/slice/bootstrap';
import { useSelector, connect } from 'react-redux';
import { trackElementEvent } from 'core/messaging/analytics/analytics';
import {
    ActivitiesPopularSubcategoriesSectionChipClickEventActivitiesFieldsV1,
    ActivitiesPopularSubcategoriesSectionSeeMoreClickEventActivitiesFieldsV1,
} from '@agoda/analytics-messages-definition-activities/events.split';
import {
    ActivitiesPopularSubcategoriesSectionChipClickEventActivitiesManifestV1,
    ActivitiesPopularSubcategoriesSectionSeeMoreClickEventActivitiesManifestV1,
} from '@agoda/analytics-messages-definition-activities/manifest/events';
import { getSubcategoryItems } from 'component/page/search/SortAndFilter/Filter/filter.utils';
import { getTopSubcategoryFilterMap, onSubcategoryFilterChange } from 'component/page/search/SortAndFilter/CategoryWithSubcategoryFilter/Subcategory.utils';
import { SubcategoryValueFilter } from 'component/page/search/SortAndFilter/CategoryFilter/CategoryFilter.types';
import { mapStateToProps, mapDispatchToProps, Props } from 'component/common/PopularSubcategoriesSection/PopularSubcategories.selectors';
import { useLocation, useNavigate } from 'react-router-dom';
import { getSortAndFilterQueryString } from 'component/page/search/SortAndFilter/sortAndFilterQuery.utils';

const PopularSubcategoriesSection: React.FC<Props> = (props) => {
    const { cms, sort, filter, filterMatrix, openCategoriesBottomSheet } = props;
    const location = useLocation();
    const navigation = useNavigate();
    const popularSubcategories = useSelector(selectTopSubcategoriesFromBootstrap);
    const subcategoryFilter = getSubcategoryItems(cms, filter.valueFilterMatrix, filterMatrix.valueFilterMatrix);
    const mappedTopSubcategories = getTopSubcategoryFilterMap(popularSubcategories || [], subcategoryFilter).map((item) => item.filterInfo);

    const onChipClick = (idx: number, subcategory: SubcategoryValueFilter) => {
        trackElementEvent<ActivitiesPopularSubcategoriesSectionChipClickEventActivitiesFieldsV1>(ActivitiesPopularSubcategoriesSectionChipClickEventActivitiesManifestV1.ids, {
            action_element_index: idx + 1,
            action_element_value: subcategory.label,
        });
        const newFilterState = onSubcategoryFilterChange(filter, subcategoryFilter, subcategory.id, !subcategory.showAsSelected);
        const query = getSortAndFilterQueryString(location.search, sort, newFilterState, true);
        navigation(`?${query}`);
    };

    const onSeeMoreClick = () => {
        trackElementEvent<ActivitiesPopularSubcategoriesSectionSeeMoreClickEventActivitiesFieldsV1>(
            ActivitiesPopularSubcategoriesSectionSeeMoreClickEventActivitiesManifestV1.ids,
            {},
            {},
        );
        openCategoriesBottomSheet();
    };

    if (!mappedTopSubcategories || mappedTopSubcategories.length === 0) {
        return null;
    }

    return (
        <Box
            data-testid="activities-popular-subcategories-section-container"
            data-element-name="activities-popular-subcategories-section-container"
            display="flex"
            mt="12"
            flexDirection="row"
            alignItems="center"
            justifyContent="space-between"
        >
            <HorizontalScroll me="8" display="flex" flexDirection="row" gap="8">
                {mappedTopSubcategories.map((subcategory: SubcategoryValueFilter, idx: number) => (
                    <Chip
                        selection={subcategory.showAsSelected ? 'selected' : 'unselected'}
                        key={subcategory.label}
                        data-testid="activities-popular-subcategories-section-chip"
                        data-element-name="activities-popular-subcategories-section-chip"
                        data-element-index={idx + 1}
                        label={subcategory.label}
                        onClick={() => onChipClick(idx, subcategory)}
                    />
                ))}
            </HorizontalScroll>
            <Box ms="8" me="4" alignItems="center" display="flex" height="30px">
                <TextLink
                    data-testid="activities-popular-subcategories-section-see-more"
                    data-element-name="activities-popular-subcategories-section-see-more"
                    trailingIcon="fill.symbol.more2"
                    label=""
                    hierarchy="tertiary"
                    onClick={onSeeMoreClick}
                />
            </Box>
        </Box>
    );
};

PopularSubcategoriesSection.displayName = 'PopularSubcategoriesSection';

export default connect(mapStateToProps, mapDispatchToProps)(PopularSubcategoriesSection);
